import isEmpty from 'lodash/isEmpty'
import flatten from 'lodash/flatten'
import find from 'lodash/find'
import includes from 'lodash/includes'
import filter from 'lodash/filter'
import map from 'lodash/map'
import {
    BATTLE_TYPES,
    RESERVE_GROUPS,
    RESERVE_PARAMETERS_TYPES,
    RESERVE_TYPES,
} from 'constants'

import { toRoman } from 'helpers'
import { sortSortieLevels } from 'helpers/battles'

const reserveBuildingMapping = {}
const reserveGroupMapping = {}

export function getBuildingTypeByReserve(reserveType, { buildings }) {
    if (isEmpty(reserveBuildingMapping)) {
        for (let buildingType in buildings) {
            if (buildings.hasOwnProperty(buildingType)) {
                reserveBuildingMapping[buildings[buildingType].reserve_type] = buildingType
            }
        }
    }

    return reserveBuildingMapping[reserveType]
}

export function getGroupByReserve(reserveType, { reserve_groups: reserveGroups }) {
    if (isEmpty(reserveGroupMapping)) {
        for (let groupName in reserveGroups) {
            if (reserveGroups.hasOwnProperty(groupName)) {
                for (let reserveTypeInGroup of reserveGroups[groupName]) {
                    reserveGroupMapping[reserveTypeInGroup] = groupName
                }
            }
        }
    }

    return reserveGroupMapping[reserveType]
}

export function groupReservesByLevel(reserves) {
    const reservesByLevel = {}
    for (let reserve of reserves) {
        if (!(reserve.level in reservesByLevel)) reservesByLevel[reserve.level] = []

        reservesByLevel[reserve.level].push(reserve)
    }

    return reservesByLevel
}

export function getSortedProductionReserves(reserves) {
    return reserves
        .filter(({ productionEnds }) => productionEnds && productionEnds > Date.now())
        .sort((a, b) => a.production_elapsed - b.production_elapsed)
}

export function getReservesQueueByLevel(reserves) {
    return getSortedProductionReserves(reserves)
        .reduce((acc, item) => {
            if (acc[item.level] === undefined) {
                acc[item.level] = {
                    order: Object.keys(acc).length,
                    productionEnds: item.productionEnds,
                }
            } else {
                acc[item.level].productionEnds = item.productionEnds
            }
            return acc
        }, {})
}

/**
 * returns true if reserve must be shown in UI.
 * it's been activated or production is in progress
 *
 * @param reserve - object
 * @returns boolean
 */
export function isReserveVisible(reserve) {
    return (
        (reserve.productionEnds === null || reserve.productionEnds < Date.now()) &&
        (reserve.effectEnds === null || reserve.effectEnds > Date.now())
    )
}

/**
 * Seeks clan reserves for first activated reserve in passed reserves list and returns it
 *
 * @param clanReserves - clanReserves from state
 * @param group - string
 * @returns object || undefined
 */
export function findFirstActiveReserveInGroup(clanReserves, reservesList) {
    return find(
        flatten(filter(
            clanReserves,
            (val, key) => includes(reservesList, key)
        )),
        reserve => reserve.effectEnds > Date.now()
    )
}

/**
 * Seeks for first producing reserve in passed reserves list and returns it
 *
 * @param clanReserves - clanReserves from state
 * @param group - string
 * @returns object || undefined
 */
export function findFirstProducingReserveInGroup(clanReserves, reservesList) {
    return find(
        flatten(filter(
            clanReserves,
            (val, key) => includes(reservesList, key)
        )),
        reserve => reserve.production_in_progress
    )
}

export function getActivatedReserve(reserves) {
    return find(flatten(map(reserves)), item => item.effectEnds > Date.now())
}

export function getCurrentLevel(activatedReserve, reserves) {
    if (activatedReserve) {
        return activatedReserve.level
    } else {
        return Math.max(...Object.keys(reserves))
    }
}

export function getReadyReserves(reserves) {
    return reserves.filter(reserve => (
        ((reserve.productionEnds === null) || (reserve.productionEnds <= Date.now())) &&
        (reserve.effectEnds === null)
    ))
}

export function getIsReservesAbsent(reservesByLevel) {
    const levels = Object.keys(reservesByLevel)
    return levels.length === 1 && reservesByLevel[levels[0]].length === 0
}

export function getTargetType(group, bonusValuesArray) {
    const economicsReserve = [RESERVE_GROUPS.DEVELOPMENT, RESERVE_GROUPS.ECONOMIC].includes(group)
    const battleReserve = (RESERVE_GROUPS.DISPOSABLE === group)
    if (battleReserve) {
        return 'battle'
    } else if (economicsReserve && bonusValuesArray.length === 2) {
        return 'economics-double'
    } else if (economicsReserve) {
        return 'economics'
    }
}

export const getReserveBonus = (reserveSettings, reserveType) => {
    const bonus = (
        reserveSettings &&
        reserveSettings[reserveType] &&
        reserveSettings[reserveType].bonus
    )
    return bonus || null
}

export const getBonusValues = (reserveBonus, level) => {
    return Object.values(reserveBonus.values[level - 1].battle_types)
}

export const sortReserveBattleTypes = (battleType1, battleType2) => {
    const battleTypeOrderMap = {
        [BATTLE_TYPES.ALL_BATTLES]: 0,
        [BATTLE_TYPES.RANDOM_BATTLES]: 1,
        [BATTLE_TYPES.RANDOM_BATTLES_AND_STEEL_HUNTER]: 1,
        [BATTLE_TYPES.CLAN_BATTLES]: 0,
        [BATTLE_TYPES.SORTIE]: 0,
        [BATTLE_TYPES.BATTLE_FOR_STRONGHOLD]: 0,
        [BATTLE_TYPES.BATTLE_FOR_STRONGHOLD_AND_SORTIE]: 0,
    }

    return Math.sign(battleTypeOrderMap[battleType1] - battleTypeOrderMap[battleType2])
}

export const sortReserveParameters = (firstParameter, secondParameter) => {
    const reserveParametersOrderMap = {
        [RESERVE_PARAMETERS_TYPES.CREW_ROLES_FACTOR]: 0,
        [RESERVE_PARAMETERS_TYPES.MAX_DAMAGE]: 0,
        [RESERVE_PARAMETERS_TYPES.COMMON_AREA_RADIUS]: 1,
        [RESERVE_PARAMETERS_TYPES.AREA_RADIUS]: 1,
        [RESERVE_PARAMETERS_TYPES.INACTIVATION_DELAY]: 2,
        [RESERVE_PARAMETERS_TYPES.DURATION]: 3,
        [RESERVE_PARAMETERS_TYPES.COMMON_DELAY]: 4,
    }

    return Math.sign(reserveParametersOrderMap[firstParameter] - reserveParametersOrderMap[secondParameter])
}

export const getReserveParameterByKey = (bonus, key) => {
    if (bonus.battle_types && bonus.battle_types.hasOwnProperty(key)) {
        return bonus.battle_types[key]
    }

    if (bonus.parameters && bonus.parameters.hasOwnProperty(key)) {
        return bonus.parameters[key]
    }

    return key === 'level' ? toRoman(bonus.level) : bonus[key]
}

export const getReserveCdsList = (cds, reserveType) => {
    if (reserveType === RESERVE_TYPES.ARTILLERY_STRIKE) {
        return cds.map(({ SORTIE: reservesCds }) => {
            return Object.keys(reservesCds)
                .sort(sortSortieLevels)
                .map((sortieLevel) => reservesCds[sortieLevel])
        })
    }

    return cds
}

export const getLevelSortedReserveSettings = (cdsCollection, clientSettings, reserveType) => {
    if (reserveType === RESERVE_TYPES.ARTILLERY_STRIKE) {
        return cdsCollection.map((levelCds) => {
            return levelCds.reduce((config, cd) => {
                const settings = clientSettings[cd]

                if (!Object.keys(config).length) {
                    return { ...settings }
                }

                Object.keys(settings).forEach((parameter) => {
                    const currentParameterValue = config[parameter]
                    const settingsParameterValue = settings[parameter]

                    if (currentParameterValue === settingsParameterValue) {
                        return
                    }

                    if (Array.isArray(currentParameterValue)) {
                        currentParameterValue.push(settings[parameter])
                    } else {
                        config[parameter] = [currentParameterValue, settingsParameterValue]
                    }
                })

                return config
            }, {})
        })
    }

    return cdsCollection.map((cd) => clientSettings[cd])
}



// WEBPACK FOOTER //
// ./helpers/reserves.js